import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  groundTruthApi, 
  GroundTruthFilters, 
  CreateGroundTruthDto, 
  UpdateGroundTruthDto 
} from 'services/api/aiReviewTest/groundTruthApi';
import { ToastManager } from 'components/organisms/CommonUI';

export const useGroundTruths = (filters?: GroundTruthFilters) => {
  const queryClient = useQueryClient();

  // 목록 조회
  const query = useQuery({
    queryKey: ['groundTruths', filters],
    queryFn: () => groundTruthApi.findAll(filters),
    staleTime: 5 * 60 * 1000, // 5분
  });


  // 생성
  const createMutation = useMutation({
    mutationFn: (data: CreateGroundTruthDto) => groundTruthApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groundTruths'] });
      ToastManager.show({
        content: 'Ground Truth가 생성되었습니다.',
        duration: 2000,
      });
    },
    onError: (error: any) => {
      if (error.response?.status === 409) {
        ToastManager.show({
          content: '이미 해당 환자와 프롬프트 타입에 대한 Ground Truth가 존재합니다.',
          duration: 3000,
        });
      } else {
        ToastManager.show({
          content: 'Ground Truth 생성에 실패했습니다.',
          duration: 3000,
        });
      }
    },
  });

  // 수정
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateGroundTruthDto }) => 
      groundTruthApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groundTruths'] });
      ToastManager.show({
        content: 'Ground Truth가 수정되었습니다.',
        duration: 2000,
      });
    },
    onError: () => {
      ToastManager.show({
        content: 'Ground Truth 수정에 실패했습니다.',
        duration: 3000,
      });
    },
  });

  // 삭제
  const deleteMutation = useMutation({
    mutationFn: (id: number) => groundTruthApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groundTruths'] });
      ToastManager.show({
        content: 'Ground Truth가 삭제되었습니다.',
        duration: 2000,
      });
    },
    onError: () => {
      ToastManager.show({
        content: 'Ground Truth 삭제에 실패했습니다.',
        duration: 3000,
      });
    },
  });

  console.log('useGroundTruths - query.data:', query.data);
  console.log('useGroundTruths - returning groundTruths:', query.data?.data?.data || []);
  
  return {
    groundTruths: query.data?.data?.data || [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    createGroundTruth: createMutation.mutate,
    updateGroundTruth: updateMutation.mutate,
    deleteGroundTruth: deleteMutation.mutate,
    isCreating: createMutation.isLoading,
    isUpdating: updateMutation.isLoading,
    isDeleting: deleteMutation.isLoading,
  };
};

// GT가 있는 환자 목록 조회
export const usePatientsWithGroundTruth = () => {
  return useQuery({
    queryKey: ['groundTruths', 'patients'],
    queryFn: async () => {
      const response = await groundTruthApi.findPatientsWithGroundTruth();
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000,
  });
};